import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================================================
// Car Color Definitions
// ============================================================================

interface CarColor {
	name: string;
	hex: string;
}

const CAR_COLORS: CarColor[] = [
	{ name: 'Black', hex: '#000000' },
	{ name: 'White', hex: '#FFFFFF' },
	{ name: 'Silver', hex: '#C0C0C0' },
	{ name: 'Gray', hex: '#808080' },
	{ name: 'Dark Gray', hex: '#505050' },
	{ name: 'Red', hex: '#CC0000' },
	{ name: 'Dark Red', hex: '#8B0000' },
	{ name: 'Burgundy', hex: '#800020' },
	{ name: 'Maroon', hex: '#800000' },
	{ name: 'Blue', hex: '#0033CC' },
	{ name: 'Dark Blue', hex: '#00008B' },
	{ name: 'Navy', hex: '#000080' },
	{ name: 'Light Blue', hex: '#87CEEB' },
	{ name: 'Green', hex: '#006400' },
	{ name: 'Dark Green', hex: '#013220' },
	{ name: 'Light Green', hex: '#90EE90' },
	{ name: 'Olive', hex: '#556B2F' },
	{ name: 'Yellow', hex: '#FFD700' },
	{ name: 'Gold', hex: '#DAA520' },
	{ name: 'Orange', hex: '#FF6600' },
	{ name: 'Brown', hex: '#654321' },
	{ name: 'Beige', hex: '#C8AD7F' },
	{ name: 'Tan', hex: '#D2B48C' },
	{ name: 'Champagne', hex: '#F7E7CE' },
	{ name: 'Pearl White', hex: '#F5F5F0' },
	{ name: 'Ivory', hex: '#FFFFF0' },
	{ name: 'Charcoal', hex: '#36454F' },
	{ name: 'Gunmetal', hex: '#2C3539' },
	{ name: 'Bronze', hex: '#CD7F32' },
	{ name: 'Copper', hex: '#B87333' },
	{ name: 'Purple', hex: '#6A0DAD' },
	{ name: 'Pink', hex: '#FF69B4' },
	{ name: 'Teal', hex: '#008080' },
];

// Helper to determine if text should be light or dark on a given background
const isLightColor = (hex: string): boolean => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
	return luminance > 0.5;
};

// ============================================================================
// Vehicle Color Autocomplete
// ============================================================================

interface VehicleColorAutocompleteProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
}

export const VehicleColorAutocomplete: React.FC<VehicleColorAutocompleteProps> = ({
	value,
	onChange,
	placeholder = 'Select color...',
	disabled = false,
	className = ''
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState(value);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);

	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

	// Filter colors based on input
	const filteredColors = React.useMemo(() => {
		if (!inputValue.trim()) return CAR_COLORS;
		const search = inputValue.toLowerCase();
		return CAR_COLORS.filter(c => c.name.toLowerCase().includes(search));
	}, [inputValue]);

	// Find the matching color for the current value (for the swatch in the input)
	const matchingColor = React.useMemo(() => {
		if (!value) return null;
		return CAR_COLORS.find(c => c.name.toLowerCase() === value.toLowerCase()) ?? null;
	}, [value]);

	const commitTypedValue = useCallback(() => {
		if (inputValue !== value) {
			onChange(inputValue);
		}
	}, [inputValue, onChange, value]);

	// Sync input value with external value
	useEffect(() => {
		setInputValue(value);
	}, [value]);

	// Handle click outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const clickedOutsideContainer = containerRef.current && !containerRef.current.contains(event.target as Node);
			const clickedOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(event.target as Node);

			if (clickedOutsideContainer && clickedOutsideDropdown) {
				setIsOpen(false);
				commitTypedValue();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [commitTypedValue]);

	// Scroll highlighted item into view
	useEffect(() => {
		if (highlightedIndex >= 0) {
			const itemElement = itemRefs.current.get(highlightedIndex);
			itemElement?.scrollIntoView({ block: 'nearest' });
		}
	}, [highlightedIndex]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setInputValue(newValue);
		setIsOpen(true);
		setHighlightedIndex(-1);
	};

	const handleSelect = useCallback((color: CarColor) => {
		setInputValue(color.name);
		onChange(color.name);
		setIsOpen(false);
		setHighlightedIndex(-1);
	}, [onChange]);

	const handleClear = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		setInputValue('');
		onChange('');
		inputRef.current?.focus();
	}, [onChange]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!isOpen) {
			if (e.key === 'ArrowDown' || e.key === 'Enter') {
				setIsOpen(true);
				e.preventDefault();
			}
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setHighlightedIndex(prev =>
					prev < filteredColors.length - 1 ? prev + 1 : prev
				);
				break;
			case 'ArrowUp':
				e.preventDefault();
				setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
				break;
			case 'Enter':
				e.preventDefault();
				if (highlightedIndex >= 0 && filteredColors[highlightedIndex]) {
					handleSelect(filteredColors[highlightedIndex]);
				} else {
					commitTypedValue();
					setIsOpen(false);
				}
				break;
			case 'Escape':
				setIsOpen(false);
				setInputValue(value);
				break;
			case 'Tab':
				commitTypedValue();
				setIsOpen(false);
				break;
		}
	};

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			<div className="relative">
				{/* Color swatch indicator in the input */}
				{matchingColor && (
					<div
						className="absolute left-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-sm border border-black/15 dark:border-white/20 flex-shrink-0"
						style={{ backgroundColor: matchingColor.hex }}
					/>
				)}
				<input
					ref={inputRef}
					type="text"
					value={inputValue}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					onFocus={() => setIsOpen(true)}
					placeholder={placeholder}
					disabled={disabled}
					className={cn(
						'flex h-9 w-full rounded-md border border-input bg-[#fafafa] dark:bg-slate-900 py-1 text-sm font-semibold shadow-xs transition-colors',
						'placeholder:text-placeholder placeholder:font-light placeholder-shown:font-normal',
						'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
						'disabled:cursor-not-allowed disabled:opacity-50',
						matchingColor ? 'pl-10' : 'px-3',
						'pr-16' // Space for icons
					)}
				/>
				<div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
					{inputValue && !disabled && (
						<button
							type="button"
							onClick={handleClear}
							className="p-1 hover:bg-accent rounded"
							aria-label="Clear selection"
						>
							<X className="w-3.5 h-3.5 text-muted-foreground" />
						</button>
					)}
					<button
						type="button"
						onClick={() => !disabled && setIsOpen(!isOpen)}
						className="p-1 hover:bg-accent rounded cursor-pointer disabled:cursor-not-allowed"
						disabled={disabled}
						aria-label={isOpen ? 'Close dropdown' : 'Open dropdown'}
					>
						<ChevronDown
							className={cn(
								'w-4 h-4 text-muted-foreground transition-transform',
								isOpen && 'rotate-180'
							)}
						/>
					</button>
				</div>
			</div>

			{/* Dropdown Portal */}
			{isOpen && (
				<ColorDropdownPortal containerRef={containerRef} dropdownRef={dropdownRef} isOpen={isOpen}>
					<div
						ref={listRef}
						className="overflow-y-auto py-1"
					>
						{filteredColors.length === 0 ? (
							<div className="py-4 text-center text-sm text-muted-foreground">
								No matching colors — press Enter to use custom value
							</div>
						) : (
							filteredColors.map((color, index) => {
								const light = isLightColor(color.hex);
								return (
									<div
										key={color.name}
										ref={(el) => {
											if (el) itemRefs.current.set(index, el);
											else itemRefs.current.delete(index);
										}}
										className={cn(
											'flex items-center gap-3 px-3 py-2 cursor-pointer text-sm',
											'hover:bg-accent hover:text-accent-foreground',
											highlightedIndex === index && 'bg-accent text-accent-foreground',
											value.toLowerCase() === color.name.toLowerCase() && 'font-medium'
										)}
										onClick={() => handleSelect(color)}
										onMouseEnter={() => setHighlightedIndex(index)}
									>
										<div
											className={cn(
												'w-6 h-6 rounded-sm border flex-shrink-0',
												light ? 'border-black/15' : 'border-white/10'
											)}
											style={{ backgroundColor: color.hex }}
										/>
										<span className="flex-1">{color.name}</span>
										{value.toLowerCase() === color.name.toLowerCase() && (
											<Check className="w-4 h-4 text-primary flex-shrink-0" />
										)}
									</div>
								);
							})
						)}
					</div>
				</ColorDropdownPortal>
			)}
		</div>
	);
};

// ============================================================================
// Dropdown Portal with Smart Positioning
// ============================================================================

interface ColorDropdownPortalProps {
	children: React.ReactNode;
	containerRef: React.RefObject<HTMLDivElement | null>;
	dropdownRef: React.RefObject<HTMLDivElement | null>;
	isOpen: boolean;
}

const ColorDropdownPortal: React.FC<ColorDropdownPortalProps> = ({ children, containerRef, dropdownRef, isOpen }) => {
	const [position, setPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
		openUpwards: false
	});

	useEffect(() => {
		const updatePosition = () => {
			if (containerRef.current && isOpen) {
				const rect = containerRef.current.getBoundingClientRect();
				const dropdownMaxHeight = 280;
				const offset = 4;
				const viewportWidth = window.innerWidth;
				const viewportHeight = window.innerHeight;

				const spaceBelow = viewportHeight - rect.bottom;
				const spaceAbove = rect.top;
				const shouldOpenUpwards = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

				let left = rect.left;
				const dropdownWidth = rect.width;

				if (rect.left + dropdownWidth > viewportWidth) {
					left = viewportWidth - dropdownWidth - 8;
				}
				if (left < 0) {
					left = 8;
				}

				setPosition({
					top: shouldOpenUpwards ? rect.top - offset : rect.bottom + offset,
					left,
					width: rect.width,
					openUpwards: shouldOpenUpwards
				});
			}
		};

		if (isOpen) {
			updatePosition();
			window.addEventListener('scroll', updatePosition, true);
			window.addEventListener('resize', updatePosition);

			return () => {
				window.removeEventListener('scroll', updatePosition, true);
				window.removeEventListener('resize', updatePosition);
			};
		}
	}, [containerRef, isOpen]);

	if (!isOpen || position.width === 0) {
		return null;
	}

	return createPortal(
		<div
			ref={dropdownRef as React.RefObject<HTMLDivElement>}
			className={cn(
				'fixed z-50 bg-popover border border-border rounded-md shadow-md max-h-[280px] overflow-y-auto',
				'animate-in fade-in-0 zoom-in-95',
				position.openUpwards ? 'origin-bottom' : 'origin-top'
			)}
			style={{
				top: position.openUpwards ? 'auto' : `${position.top}px`,
				bottom: position.openUpwards ? `${window.innerHeight - position.top}px` : 'auto',
				left: `${position.left}px`,
				width: `${position.width}px`
			}}
			onMouseDown={(e) => e.stopPropagation()}
		>
			{children}
		</div>,
		document.body
	);
};
